<template>
  <div :id="elementId"></div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { loadScript } from 'liyu-pc-base/utils/load-async-asset';
import { get_upload_files_url, upload_file } from 'liyu-pc-base/api/nsbcs-model';
import { message } from 'ant-design-vue';
import { useEnv } from 'liyu-pc-base/init-plugin';
import { useI18n } from 'vue-i18n';
import { useUserStore } from 'liyu-pc-base/init-plugin';

const get_BASE_URL = () => {
  const env = useEnv();
  return env.VUE_APP_PUBLIC_PATH + 'tinymce';
};

function extname(filename: string | undefined) {
  if (!filename || typeof filename != 'string') {
    return false;
  }
  const a = filename.split('').reverse().join('');
  const b = a.substring(0, a.search(/\./)).split('').reverse().join('');
  return b;
}

export default defineComponent({
  name: 'TinymceEditor',
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    height: {
      type: Number,
      default: 500,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    // 复用 upload.vue 的属性，用于文件上传
    group: {
      type: String,
      default: 'sys',
    },
    access_type: {
      type: String,
      default: 'public',
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const userStore = useUserStore();
    const current_org = userStore.currentOrg; // 当前组织org
    const { locale } = useI18n();
    const elementId = 'tinymce-editor-' + Date.now() + Math.random().toString(36).substring(2);
    const editorInstance = ref<any>(null);
    const isInitializing = ref(true); // 防止 watch 在初始化时触发

    // 图片上传处理
    const images_upload_handler = (blobInfo: any) =>
      new Promise(async (resolve, reject) => {
        try {
          const file = blobInfo.blob();
          const filename = blobInfo.filename();
          const filetype = extname(filename);

          // 1. 获取上传参数
          const res = await get_upload_files_url({
            access_type: props.access_type,
            filetype: filetype,
            filename: filename,
            group: props.group,
            author: 0, // 暂时写死，可根据需要修改
          });

          // 2. 构建 FormData
          const formData = new FormData();
          formData.append('file', file, filename);
          formData.append('file_id', res.params.file_id);
          formData.append('key', res.params.key);

          // 3. 上传文件
          await upload_file(formData);

          // 4. 返回文件 URL
          message.success('图片上传成功');
          let url = res.params.url;
          if (url.includes('?')) {
            url = `${url}&org_id=${current_org.id}`;
          } else {
            url = `${url}?org_id=${current_org.id}`;
          }
          resolve(res.params.url);
        } catch (error: any) {
          message.error('图片上传失败: ' + (error.message || '未知错误'));
          reject(error);
        }
      });

    const initTinymce = async () => {
      if (!window.tinymce) {
        console.error('Tinymce script not loaded!');
        return;
      }
      let language = locale.value.replace('-', '_');
      console.log('Tinymce language:', language);
      let language_url = '';
      if (language == 'zh_CN') {
        language_url = `${get_BASE_URL()}/langs/${language}.js`;
      } else {
        language = null;
      }
      window.tinymce.init({
        selector: `#${elementId}`,
        height: props.height,
        plugins: 'lists link image table code help wordcount',
        toolbar:
          'undo redo | blocks | bold italic forecolor backcolor | ' +
          'alignleft aligncenter alignright alignjustify | ' +
          'bullist numlist outdent indent | image | removeformat | help',
        promotion: false, // 隐藏 "Upgrade" 按钮
        branding: false, // 隐藏 "Powered by Tiny"
        menubar: true,
        language: language,
        language_url: language_url,
        automatic_uploads: true, // 启用自动上传
        images_upload_handler: images_upload_handler, // 挂载上传处理函数
        file_picker_types: 'image', // 限制文件选择器只选择图片
        ...props.options,
        setup: editor => {
          editorInstance.value = editor;
          editor.on('init', () => {
            isInitializing.value = false;
            editor.setContent(props.modelValue || '');
          });
          editor.on('change keyup', () => {
            const content = editor.getContent();
            // 避免在初始化时就触发无意义的 update
            if (!isInitializing.value) {
              emit('update:modelValue', content);
            }
          });
        },
      });
    };

    onMounted(async () => {
      try {
        if (!window.tinymce) {
          await loadScript(get_BASE_URL() + '/tinymce.min.js');
        }
        await initTinymce();
      } catch (error) {
        console.error('Failed to load or initialize Tinymce:', error);
      }
    });

    onBeforeUnmount(() => {
      if (editorInstance.value) {
        window.tinymce.remove(editorInstance.value);
        editorInstance.value = null;
      }
    });

    watch(
      () => props.modelValue,
      newValue => {
        if (
          editorInstance.value &&
          !isInitializing.value &&
          newValue !== editorInstance.value.getContent()
        ) {
          editorInstance.value.setContent(newValue || '');
        }
      },
    );

    return {
      elementId,
    };
  },
});
</script>

<style scoped>
/* 你可以在这里添加一些组件的局部样式 */
</style>
